import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useEligibilityAccess } from '@features/billing/hooks/useEligibilityAccess';
import { useCan } from '@features/user';
import { Alert } from '@mui/material';

import { useBillingSettingsAccess } from '../hooks/useBillingSettingsAccess';
import {
  billingSettingsPages,
  type BillingSettingsSection,
  isBillingSettingsSection,
} from '../navigation/billingSettingsRoutes';

import BillingSettingsPage from './BillingSettingsPage';

/** Separate practice pages, with redirects for existing setup links and bookmarks. */
export function PracticeBillingSettingsPage() {
  const { billingSection } = useParams();
  const [searchParams] = useSearchParams();
  const { hasAccess: billingEnabled, isLoading } = useBillingSettingsAccess();
  const { hasAccess: eligibilityEnabled } = useEligibilityAccess();
  const manageBilling = useCan('manage_billing');
  const manageEligibility = useCan('manage_eligibility');
  const configureCompensation = useCan('configure_compensation');
  if (isLoading) return null;

  const oldTab = searchParams.get('tab');
  if (!billingSection && isBillingSettingsSection(oldTab)) {
    const params = new URLSearchParams(searchParams);
    params.delete('tab');
    return (
      <Navigate
        to={{ pathname: billingSettingsPages[oldTab].path, search: params.toString() }}
        replace
      />
    );
  }

  const section: BillingSettingsSection | undefined = !billingSection
    ? 'overview'
    : billingSection === 'payment-connection'
      ? 'patient-payments'
      : isBillingSettingsSection(billingSection)
        ? billingSection
        : undefined;
  if (!section) return <Alert severity="info">This billing setting does not exist.</Alert>;
  if (
    billingSection &&
    billingSettingsPages[section].path !== `/practice/billing-settings/${billingSection}`
  ) {
    return <Navigate to={billingSettingsPages[section].path} replace />;
  }

  if (
    section === 'overview' &&
    !billingEnabled &&
    eligibilityEnabled &&
    (manageBilling || manageEligibility)
  ) {
    return <Navigate to={billingSettingsPages.identities.path} replace />;
  }
  const allowed =
    section === 'identities'
      ? (manageBilling && billingEnabled) ||
        (eligibilityEnabled && (manageBilling || manageEligibility))
      : manageBilling &&
        (section === 'patient-payments' || billingEnabled) &&
        (section !== 'compensation' || configureCompensation);
  if (!allowed)
    return <Alert severity="info">This billing setting is not available for your account.</Alert>;

  return <BillingSettingsPage key={section} section={section} />;
}
